/**
 * Amplitude Analytics Service
 *
 * Manages analytics for two contexts:
 * - PRIVATE: Authenticated venue managers (identified by userId)
 * - PUBLIC: Anonymous QR menu customers (identified by sessionId, grouped by venue)
 */

// SDK is loaded (and conditionally initialized) via CDN script in index.html
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare global {
  interface Window {
    amplitude: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  }
}

export enum AnalyticsContext {
  PRIVATE = 'PRIVATE', // Manager dashboard/authenticated area
  PUBLIC = 'PUBLIC', // QR code menu/customer area
}

/**
 * Currently active club/venue, kept in sync by the init/update functions below.
 * Injected as an event property on every tracked event so analyses can break down
 * by venue without relying on group-level analytics (limited on lower plans).
 */
let currentClubId: string | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function decodeJWT(token: string): Record<string, any> {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('[Amplitude] Error decoding JWT:', error);
    return {};
  }
}

/**
 * Initialize Amplitude for the PRIVATE (manager) context
 * Called when manager first accesses authenticated pages (MenusPage, etc.)
 *
 * @param userId - Unique identifier of the logged-in manager (must be 5+ chars)
 * @param clubId - ID of the club/venue the manager is associated with
 */
export function initPrivateAnalytics(userId: string, clubId: string): void {
  if (!window.amplitude) {
    console.warn('Amplitude SDK not initialized. Skipping private analytics setup.');
    return;
  }

  try {
    if (!userId || userId.length < 5) {
      console.warn(
        `[Amplitude] userId "${userId}" is too short (min 5 chars). Using default identifier.`,
      );
      userId = `user_${clubId}`;
    }

    window.amplitude.setUserId(userId);
    currentClubId = clubId;

    const identifyObj = new window.amplitude.Identify();
    identifyObj.set('context', AnalyticsContext.PRIVATE);
    identifyObj.set('userRole', 'manager');
    identifyObj.set('clubId', clubId);

    window.amplitude.identify(identifyObj);

    window.amplitude.setGroup('club', clubId);

    const groupIdentifyObj = new window.amplitude.Identify();
    groupIdentifyObj.set('type', 'club');
    groupIdentifyObj.set('name', clubId);

    window.amplitude.groupIdentify('club', clubId, groupIdentifyObj);

    console.log(`[Amplitude] Private analytics initialized for user ${userId} in club ${clubId}`);
  } catch (error) {
    console.error('[Amplitude] Error initializing private analytics:', error);
  }
}

/**
 * Initialize Amplitude for the PUBLIC (QR menu customer) context
 * Called when entering the QR code menu routes
 *
 * @param clubId - ID of the venue/club being viewed
 * @param tableId - ID of the table (optional, for venue-specific tracking)
 */
export function initPublicAnalytics(clubId: string, tableId?: string): void {
  if (!window.amplitude) {
    console.warn('Amplitude SDK not initialized. Skipping public analytics setup.');
    return;
  }

  try {
    currentClubId = clubId;

    // No setUserId: public visitors stay anonymous
    const identifyObj = new window.amplitude.Identify();
    identifyObj.set('context', AnalyticsContext.PUBLIC);
    identifyObj.set('userRole', 'customer');
    identifyObj.set('clubId', clubId);
    if (tableId) {
      identifyObj.set('tableId', tableId);
    }

    window.amplitude.identify(identifyObj);

    window.amplitude.setGroup('club', clubId);

    const groupIdentifyObj = new window.amplitude.Identify();
    groupIdentifyObj.set('type', 'club');
    groupIdentifyObj.set('venueId', clubId);

    window.amplitude.groupIdentify('club', clubId, groupIdentifyObj);

    console.log(
      `[Amplitude] Public analytics initialized for club ${clubId}` +
        (tableId ? ` and table ${tableId}` : ''),
    );
  } catch (error) {
    console.error('[Amplitude] Error initializing public analytics:', error);
  }
}

/**
 * Track an event in the PRIVATE (manager) context
 * Automatically includes context and club information
 *
 * @param eventName - Name of the event to track
 * @param properties - Optional event properties
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function trackManagerAction(
  eventName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: Record<string, any>,
): void {
  if (!window.amplitude) {
    console.warn('Amplitude SDK not initialized. Skipping event tracking.');
    return;
  }

  try {
    const eventProperties = {
      context: AnalyticsContext.PRIVATE,
      ...(currentClubId ? { clubId: currentClubId } : {}),
      ...properties,
    };

    window.amplitude.track(eventName, eventProperties);
    console.log(`[Amplitude] Manager event tracked: ${eventName}`, eventProperties);
  } catch (error) {
    console.error(`[Amplitude] Error tracking manager event ${eventName}:`, error);
  }
}

/**
 * Track an event in the PUBLIC (customer) context
 * Automatically includes context and club information
 *
 * @param eventName - Name of the event to track
 * @param properties - Optional event properties
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function trackCustomerAction(
  eventName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: Record<string, any>,
): void {
  if (!window.amplitude) {
    console.warn('Amplitude SDK not initialized. Skipping event tracking.');
    return;
  }

  try {
    const eventProperties = {
      context: AnalyticsContext.PUBLIC,
      ...(currentClubId ? { clubId: currentClubId } : {}),
      ...properties,
    };

    window.amplitude.track(eventName, eventProperties);
    console.log(`[Amplitude] Customer event tracked: ${eventName}`, eventProperties);
  } catch (error) {
    console.error(`[Amplitude] Error tracking customer event ${eventName}:`, error);
  }
}

/**
 * Reset Amplitude on logout
 * Clears user identification for the private context
 */
export function resetAnalytics(): void {
  if (!window.amplitude) {
    console.warn('Amplitude SDK not initialized. Skipping reset.');
    return;
  }

  try {
    currentClubId = null;
    window.amplitude.setUserId(null);
    window.amplitude.reset();
    console.log('[Amplitude] Analytics reset on logout');
  } catch (error) {
    console.error('[Amplitude] Error resetting analytics:', error);
  }
}

/**
 * Update club context when manager switches clubs
 * Used when a manager with multiple clubs switches to a different one
 *
 * @param newClubId - ID of the new club
 */
export function updateClubContext(newClubId: string): void {
  if (!window.amplitude) {
    console.warn('Amplitude SDK not initialized. Skipping context update.');
    return;
  }

  try {
    currentClubId = newClubId;

    const identifyObj = new window.amplitude.Identify();
    identifyObj.set('clubId', newClubId);
    window.amplitude.identify(identifyObj);

    window.amplitude.setGroup('club', newClubId);

    const groupIdentifyObj = new window.amplitude.Identify();
    groupIdentifyObj.set('type', 'club');
    groupIdentifyObj.set('name', newClubId);

    window.amplitude.groupIdentify('club', newClubId, groupIdentifyObj);

    console.log(`[Amplitude] Club context updated to ${newClubId}`);
  } catch (error) {
    console.error('[Amplitude] Error updating club context:', error);
  }
}

/**
 * Initialize private analytics from JWT token and clubId
 * Convenience function that extracts userId from stored token
 *
 * @param clubId - ID of the club
 */
export function initPrivateAnalyticsFromToken(clubId: string): void {
  try {
    const token = localStorage.getItem('auth@token');
    if (!token) {
      console.warn('[Amplitude] No auth token found. Cannot initialize private analytics.');
      return;
    }

    const decoded = decodeJWT(token);
    initPrivateAnalytics(decoded.sub, clubId);
  } catch (error) {
    console.error('[Amplitude] Error initializing private analytics from token:', error);
  }
}
